import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, take } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { ConnectAccountStatusResponse, StripeConnectService } from './stripe-connect.service';

export type ConnectAccountStatus = ConnectAccountStatusResponse['status'];
export type TreasuryDisplayStatus = ConnectAccountStatus | 'error' | 'forbidden';

export interface TreasuryAccountInfo {
    id: number;
    bankAccountId: number | null;
    stripeFinancialAccountId: string;
    status: 'open' | 'closed';
    friendlyName?: string;
}

export interface TreasuryBalance {
    available: number;
    pending: number;
    outboundPending: number;
    totalPending: number;
    currency: string;
}

export interface TreasuryFinancialAddresses {
    aba?: {
        routingNumber: string;
        accountNumber: string;
        accountNumberLast4: string;
        bankName: string;
        accountHolderName: string;
    };
}

export interface PendingItemsSummary {
    count: number;
    total: number;
}

export interface TreasuryBalanceResponse {
    treasury: TreasuryAccountInfo | null;
    balance: TreasuryBalance | null;
    financialAddresses: TreasuryFinancialAddresses | null;
    pendingItems?: PendingItemsSummary | null;
    failedItems?: PendingItemsSummary | null;
    onboardingRequired?: boolean;
    lastUpdatedAt: string | null;
}

export interface RecentActivityTransaction {
    id: string;
    type: string;
    typeLabel: string;
    description: string;
    detail: string | null;
    status: string;
    statusLabel: string;
    amount: number;
    currency: string;
    isCredit: boolean;
    createdAt: string;
    linkedTransactionId: string | null;
    disputeStatus: string | null;
    disputeFiledAt: string | null;
    disputeResolvedAt: string | null;
}

export interface RecentActivityResponse {
    transactions: RecentActivityTransaction[];
    hasMore: boolean;
    viewAllUrl: string;
}

export interface PendingTransfer {
    id: number;
    type: string;
    typeLabel: string;
    description: string;
    status: string;
    statusLabel: string;
    amount: number;
    currency: string;
    isCredit: boolean;
    createdAt: string;
    failureCode: string | null;
    failureMessage: string | null;
    returnCode: string | null;
    returnMessage: string | null;
}

export interface PendingTransfersResponse {
    data: PendingTransfer[];
    failed: PendingTransfer[];
}

export interface StatisticsBreakdownItem {
    accountCode: string;
    accountName: string;
    amount: number;
    percentage: number;
}

export interface StatisticsResponse {
    totalRevenue: number;
    currency: string;
    period: string;
    breakdown: StatisticsBreakdownItem[];
}

export interface StatisticsParams {
    period: 'this_month' | 'last_month' | 'custom';
    startDate?: string;
    endDate?: string;
}

export interface TransactionDetailAuditEvent {
    event: string;
    timestamp: string;
    actor: string;
}

export interface TransactionDetailBalanceImpact {
    cash: number;
    inboundPending: number;
    outboundPending: number;
}

export interface TransactionDetail {
    id: number;
    stripeTransactionId: string;
    type: string;
    typeLabel: string;
    status: string;
    statusLabel: string;
    amount: number;
    currency: string;
    description: string;
    isCredit: boolean;
    effectiveAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    balanceImpact: TransactionDetailBalanceImpact | null;
    flowDetails: Record<string, any> | null;
    metadata: Record<string, any> | null;
    auditTrail: TransactionDetailAuditEvent[];
    ledgerTransactionId: number | null;
}

export interface PendingTransferAlert {
    direction: 'inbound' | 'outbound';
    amountCents: number;
}

export interface ConnectedBankAccount {
    id: number;
    friendlyName: string;
    last4: string;
    routingNumber: string | null;
    balance: number | null;
    isBalanceLinked: boolean;
    stripeFinancialConnectionsId: string | null;
    stripePaymentMethodId: string | null;
    /** Transient UI state — true while a link-balance request is in flight */
    linkingBalance?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class TreasuryService {
    /** Current Connect account onboarding status */
    private readonly _connectStatus$ = new BehaviorSubject<TreasuryDisplayStatus | null>(null);
    readonly connectStatus$ = this._connectStatus$.asObservable();

    /** Treasury balance and account data from the balance endpoint */
    private readonly _balanceResponse$ = new BehaviorSubject<TreasuryBalanceResponse | null>(null);
    readonly balanceResponse$ = this._balanceResponse$.asObservable();

    /** Recent activity transactions for the balance card */
    private readonly _recentActivity$ = new BehaviorSubject<RecentActivityResponse | null>(null);
    readonly recentActivity$ = this._recentActivity$.asObservable();

    /** Connected (external) bank accounts linked via Financial Connections */
    private readonly _connectedAccounts$ = new BehaviorSubject<ConnectedBankAccount[] | null>(null);
    readonly connectedAccounts$ = this._connectedAccounts$.asObservable();

    /** Emits when the user requests to resume/start verification */
    private readonly _resumeVerification$ = new Subject<void>();
    readonly resumeVerification$ = this._resumeVerification$.asObservable();

    /** Emits when connected accounts should be re-fetched (e.g., after linking a new account) */
    private readonly _refreshConnectedAccounts$ = new Subject<void>();
    readonly refreshConnectedAccounts$ = this._refreshConnectedAccounts$.asObservable();

    /** Pending transfer alert shown on the dashboard after submitting a transfer */
    private readonly _pendingTransferAlert$ = new BehaviorSubject<PendingTransferAlert | null>(null);
    readonly pendingTransferAlert$ = this._pendingTransferAlert$.asObservable();

    /** Emits when the dashboard should re-fetch balance + activity (e.g., after websocket event) */
    private readonly _refreshDashboardData$ = new Subject<void>();
    readonly refreshDashboardData$ = this._refreshDashboardData$.asObservable();

    /** Track which org's connect status is cached to avoid stale cross-org data */
    private _connectStatusOrgId: number | null = null;

    /** Whether a prefetch API call is currently in flight */
    private _prefetchInFlight = false;

    constructor(
        private _http: ApplicationHttpClient,
        private _stripeConnectService: StripeConnectService,
    ) {}

    /**
     * Reset state when entering the Treasury view to prevent stale data
     * from a previous organization leaking across org switches.
     *
     * Connect status is intentionally NOT cleared here — it persists from
     * prefetch and is only refreshed on org switch via prefetchConnectStatus().
     */
    resetState(): void {
        this._balanceResponse$.next(null);
        this._recentActivity$.next(null);
        this._connectedAccounts$.next(null);
        this._pendingTransferAlert$.next(null);
    }

    /**
     * Prefetch the Connect account status for an organization.
     * Called from the app shell (v2-layout) to eliminate the blocking API call
     * when the user navigates to Treasury.
     */
    prefetchConnectStatus(organizationId: number): void {
        // Already have status for this org
        if (this._connectStatusOrgId === organizationId && this._connectStatus$.getValue() !== null) {
            return;
        }

        // Already fetching for this org
        if (this._connectStatusOrgId === organizationId && this._prefetchInFlight) {
            return;
        }

        this._fetchConnectStatus(organizationId);
    }

    /**
     * Force a re-fetch of the Connect account status, bypassing the prefetch cache guards.
     * Use after actions that may change KYC state (e.g. Stripe onboarding completion) so
     * subscribers see the latest status without waiting for a navigation or org switch.
     */
    refreshConnectStatus(organizationId: number): void {
        this._fetchConnectStatus(organizationId);
    }

    private _fetchConnectStatus(organizationId: number): void {
        this._connectStatusOrgId = organizationId;
        this._prefetchInFlight = true;
        this._connectStatus$.next(null);

        this._stripeConnectService.getConnectAccountStatus(organizationId).pipe(
            take(1),
        ).subscribe({
            next: (response) => {
                this._prefetchInFlight = false;
                if (this._connectStatusOrgId === organizationId) {
                    this._connectStatus$.next(response.status);
                }
            },
            error: (err: { status?: number }) => {
                this._prefetchInFlight = false;
                if (this._connectStatusOrgId === organizationId) {
                    this._connectStatus$.next(err?.status === 403 ? 'forbidden' : 'error');
                }
            },
        });
    }

    /**
     * Update the Connect account onboarding status
     */
    setConnectStatus(status: TreasuryDisplayStatus | null): void {
        this._connectStatus$.next(status);
    }

    /**
     * Update the treasury balance response
     */
    setBalanceResponse(response: TreasuryBalanceResponse | null): void {
        this._balanceResponse$.next(response);
    }

    /**
     * Update the recent activity response
     */
    setRecentActivity(response: RecentActivityResponse | null): void {
        this._recentActivity$.next(response);
    }

    /**
     * Update the connected bank accounts
     */
    setConnectedAccounts(accounts: ConnectedBankAccount[] | null): void {
        this._connectedAccounts$.next(accounts);
    }

    /**
     * Signal that the user wants to resume verification
     */
    resumeVerification(): void {
        this._resumeVerification$.next();
    }

    /**
     * Signal that connected accounts should be re-fetched
     */
    requestConnectedAccountsRefresh(): void {
        this._refreshConnectedAccounts$.next();
    }

    /**
     * Set the pending transfer alert (shown on dashboard after submitting a transfer)
     */
    setPendingTransferAlert(alert: PendingTransferAlert | null): void {
        this._pendingTransferAlert$.next(alert);
    }

    /**
     * Signal that the dashboard should re-fetch balance + activity
     */
    requestDashboardRefresh(): void {
        this._refreshDashboardData$.next();
    }

    /**
     * Gets the treasury balance and account status for an organization.
     * Returns onboardingRequired: true if no treasury account exists.
     */
    public getBalance(organizationId: number): Observable<TreasuryBalanceResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.Balance(organizationId),
        );
        return this._http.get<TreasuryBalanceResponse>(url);
    }

    /**
     * Gets the most recent transactions for the balance card.
     */
    public getRecentActivity(organizationId: number): Observable<RecentActivityResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.RecentActivity(organizationId),
        );
        return this._http.get<RecentActivityResponse>(url);
    }

    /**
     * Gets pending/processing transfers for the pending transfers dialog.
     * Pass includeFailed=true to also fetch terminal-state (failed/canceled/returned) transfers.
     */
    public getPendingTransfers(organizationId: number, includeFailed = false): Observable<PendingTransfersResponse> {
        let url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.PendingTransfers(organizationId),
        );
        if (includeFailed) {
            url += '?include=failed';
        }
        return this._http.get<PendingTransfersResponse>(url);
    }

    /**
     * Dismiss a failed transfer from the attention list.
     */
    public dismissTransfer(organizationId: number, type: string, transferId: number): Observable<void> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.PendingTransferDismiss(organizationId, type, transferId),
        );
        return this._http.post<void>(url, '');
    }

    /**
     * Retry a failed transfer by creating a new one with the same parameters.
     */
    public retryTransfer(organizationId: number, type: string, transferId: number): Observable<{ data: PendingTransfer }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.PendingTransferRetry(organizationId, type, transferId),
        );
        return this._http.post<{ data: PendingTransfer }>(url, '');
    }

    /**
     * Gets revenue statistics for an organization, broken down by GL account category.
     */
    public getStatistics(organizationId: number, params: StatisticsParams): Observable<StatisticsResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.Statistics(organizationId),
        );
        let queryParams = new HttpParams().set('period', params.period);
        if (params.startDate) {
            queryParams = queryParams.set('startDate', params.startDate);
        }
        if (params.endDate) {
            queryParams = queryParams.set('endDate', params.endDate);
        }
        return this._http.get<StatisticsResponse>(url, { params: queryParams });
    }

    /**
     * Gets the full detail for a single Treasury transaction.
     */
    public getTransactionDetail(organizationId: number, transactionId: number): Observable<TransactionDetail> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.Transaction(organizationId, transactionId),
        );
        return this._http.get<TransactionDetail>(url);
    }

}
